import type { FinlabDataApi, HistoryQuery, HistoryResponse, HistoryRow, Quote } from '@sdk';
import { debugLog } from './debug';

function asNum(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function pick(obj: Record<string, unknown>, ...keys: string[]): unknown {
  for (const k of keys) {
    if (obj[k] != null && obj[k] !== '') return obj[k];
  }
  return undefined;
}

function day(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v.slice(0, 10);
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v.toISOString().slice(0, 10);
  return String(v).slice(0, 10);
}

function normalizeRow(raw: unknown): HistoryRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const tradeDate = day(pick(o, 'tradeDate', 'trade_date', 'date', 'Date', 't'));
  if (!/^\d{4}-\d{2}-\d{2}$/.test(tradeDate)) return null;
  return {
    tradeDate,
    open: asNum(pick(o, 'open', 'Open')),
    high: asNum(pick(o, 'high', 'High')),
    low: asNum(pick(o, 'low', 'Low')),
    close: asNum(pick(o, 'close', 'Close')),
    adjustedClose: asNum(pick(o, 'adjustedClose', 'adjusted_close', 'adjClose', 'adj_close')),
    volume: asNum(pick(o, 'volume', 'Volume')),
    source: (pick(o, 'source', 'provider') as string | undefined) || undefined,
  };
}

function extractRows(json: unknown): unknown[] {
  if (Array.isArray(json)) return json;
  if (!json || typeof json !== 'object') return [];
  const o = json as Record<string, unknown>;
  if (Array.isArray(o.rows)) return o.rows;
  if (Array.isArray(o.bars)) return o.bars;
  if (Array.isArray(o.data)) return o.data;
  if (Array.isArray(o.history)) return o.history;
  return [];
}

function qs(params: Record<string, string | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v) sp.set(k, v);
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

async function aggGet(path: string): Promise<unknown> {
  const started = Date.now();
  const res = await fetch(`/api/agg${path}`);
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = { error: text || `HTTP ${res.status}` };
  }
  const ms = Date.now() - started;
  const obj = json && typeof json === 'object' ? (json as Record<string, unknown>) : {};
  debugLog(res.ok ? 'info' : 'error', 'agg', `${res.status} GET ${path} (${ms}ms)`, {
    bytes: text.length,
    keys: Object.keys(obj),
    rows: Array.isArray(obj.rows) ? obj.rows.length : undefined,
    error: obj.error,
  });
  if (!res.ok) {
    throw new Error(String(obj.error || obj.message || `HTTP ${res.status}`));
  }
  return json;
}

function normalizeQuote(raw: Record<string, unknown>, symbol: string): Quote {
  return {
    symbol,
    tradeDate: day(pick(raw, 'tradeDate', 'trade_date')) || null,
    close: asNum(raw.close),
    adjustedClose: asNum(pick(raw, 'adjustedClose', 'adjusted_close')),
    source: (raw.source as string | null) ?? null,
    cacheHit: Boolean(raw.cacheHit ?? raw.cache_hit),
    stale: Boolean(raw.stale),
  };
}

export const data: FinlabDataApi = {
  async healthDeep() {
    const res = await fetch('/health/deep');
    const json = await res.json();
    debugLog(res.ok ? 'info' : 'warn', 'health', `deep ${res.status}`, {
      aggregator_reachable: json.aggregator_reachable,
      aggregator_configured: json.aggregator_configured,
    });
    return json;
  },

  async history(symbol: string, query: HistoryQuery): Promise<HistoryResponse> {
    const path = `/price/${encodeURIComponent(symbol)}/history${qs({
      from: query.from,
      to: query.to,
      assetClass: query.assetClass,
      region: query.region,
    })}`;
    const json = await aggGet(path);
    const rawRows = extractRows(json);
    const rows = rawRows.map(normalizeRow).filter((r): r is HistoryRow => r != null);
    const obj = (json && typeof json === 'object' ? json : {}) as Record<string, unknown>;
    if (obj.error && !rows.length) {
      throw new Error(String(obj.error));
    }
    debugLog('info', 'history', `${symbol} ${query.from}→${query.to}`, {
      raw: rawRows.length,
      kept: rows.length,
      sample: rows[0] ?? null,
      dropped: rawRows.length - rows.length,
    });
    return {
      symbol: String(obj.symbol || symbol),
      from: String(obj.from || query.from),
      to: String(obj.to || query.to),
      rows,
      cacheHit: Boolean(obj.cacheHit ?? obj.cache_hit),
      gapsFound: asNum(obj.gapsFound ?? obj.gaps_found) ?? undefined,
      error: obj.error ? String(obj.error) : undefined,
    };
  },

  async quote(symbol: string, query = {}): Promise<Quote> {
    const path = `/price/${encodeURIComponent(symbol)}${qs({
      assetClass: query.assetClass,
      region: query.region,
    })}`;
    const json = (await aggGet(path)) as Record<string, unknown>;
    if (json?.error && json.close == null && json.tradeDate == null && json.trade_date == null) {
      throw new Error(String(json.error));
    }
    return normalizeQuote(json, symbol);
  },

  async instrument(symbol: string) {
    try {
      return (await aggGet(`/instruments/${encodeURIComponent(symbol)}`)) as Record<string, unknown>;
    } catch {
      return null;
    }
  },

  async macro(series: string, provider = 'fred') {
    return aggGet(`/macro${qs({ series, provider })}`);
  },
};
